import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import {
  type MenuProps as MUIMenuProps,
  SimpleMenu,
  withNavigationMenuStyle,
} from "../../../common/components/Menu/Menu";
import { MenuSpacer } from "../../../common/components/Menu/MenuItem";
import { MENU_POS_RIGHT } from "../../../common/components/Menu/MenuPositions";
import { NOTIFICATIONS_PAGE, NOTIFICATIONS_SETTINGS_PAGE } from "../../../utils/url/staticUrls";
import { IMG_EMPTY_NOTIFICATIONS } from "../../../utils/url/staticResources";
import { NotificationsItem } from "./NotificationItem";
import { type NotificationDTO } from "../../../Notifications/api/dto";
import { markNotificationsAsReadThunk, openNotificationsMenuThunk } from "../../../Notifications/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { selectIsNotificationsLoading, selectNotifications } from "../../../Notifications/redux/NotificationsSelectors";
import Spinner from "../../../common/components/Spinner/Spinner";
import { type NotificationContext, Notifications } from "~/Notifications/Messages";
import { useTranslation } from "react-i18next";

interface MenuProps extends MUIMenuProps {
  onClose: () => void;
  beforeOptionsChildren?: ReactNode;
  children?: never;
}

const MENU_NOTIFICATION_AMOUNT = 3;

const StyledMenu = withNavigationMenuStyle(SimpleMenu);

export const NotificationsMenu = ({ onClose, beforeOptionsChildren, open, ...props }: MenuProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch(openNotificationsMenuThunk());
    }
  }, [dispatch, open]);

  const isLoading = useSelector(selectIsNotificationsLoading);
  const allNotifications = useSelector(selectNotifications);

  const results = useMemo(() => allNotifications.slice(0, MENU_NOTIFICATION_AMOUNT), [allNotifications]);

  const readNotification = useCallback(
    async (item: NotificationDTO) => dispatch(markNotificationsAsReadThunk(item.uid, !item.read)),
    [dispatch],
  );

  const context: NotificationContext = {
    markAsRead: async (data) => {
      if (!data.read) {
        // TODO FIXME
        await readNotification(data as NotificationDTO);
      }
    },
  };

  return (
    <StyledMenu
      onClose={onClose}
      open={open}
      {...MENU_POS_RIGHT}
      {...props}
      MenuListProps={{
        disablePadding: true,
        // @ts-ignore
        "data-ta-spec": "notification-main-box",
      }}
      className="scha__personal-nav__notifications-menu"
    >
      {beforeOptionsChildren}
      <div className="scha__personal-nav__notifications-menu__header">
        <span className="title-medium">{t("header.link.my.notification")}</span>
        <a href={NOTIFICATIONS_PAGE} data-ta-spec={"notification-view-all"}>
          {t("notifications.viewAll")}
        </a>
      </div>
      <MenuSpacer fullwidth={true} />
      <div className="scha__personal-nav__notifications-menu__list">
        {isLoading && <Spinner className="mb-3" />}
        {!isLoading &&
          results.map((item, i) => (
            <div key={i} data-ta-title={Notifications.renderTitleGA(item, context)} data-ta-spec={"notification-block"}>
              <NotificationsItem notification={item} readMessage={readNotification} />
              {i < results.length - 1 && <MenuSpacer fullwidth={true} />}
            </div>
          ))}
        {!isLoading && !results.length && (
          <div className="scha__personal-nav__notifications-menu__list-no-results">
            <img src={IMG_EMPTY_NOTIFICATIONS} alt="no notifications" />
            <span className="headline-small">{t("notification.noResults.title")}</span>
            <span className="label-large mb-3">{t("notification.noResults.description")}</span>
            <a href={NOTIFICATIONS_SETTINGS_PAGE} className="label-large" data-ta-spec={"notification-settings"}>
              {t("notification.settings")}
            </a>
          </div>
        )}
      </div>
    </StyledMenu>
  );
};
